import React, { useState, useCallback, useRef, useEffect } from 'react';
import { host } from '../script/variables';

/* ─────────────────────────────────────────────
   Inline styles using the design-system variables
   (assumes the CSS vars are on :root globally)
───────────────────────────────────────────── */
const S = {
  page: {
    minHeight: '100vh',
    background: 'var(--ad-bg)',
    color: 'var(--ad-text)',
    fontFamily: 'var(--ad-font)',
    padding: '2rem 1rem',
  },
  container: {
    maxWidth: 900,
    margin: '0 auto',
  },

  /* ── Header ── */
  header: {
    marginBottom: '2rem',
  },
  headerLabel: {
    fontSize: '0.72rem',
    fontFamily: 'var(--ad-mono)',
    color: 'var(--ad-amber)',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: '0.35rem',
  },
  headerTitle: {
    fontSize: 'clamp(1.5rem, 4vw, 2rem)',
    fontWeight: 700,
    color: 'var(--ad-text)',
    margin: 0,
  },
  headerSub: {
    marginTop: '0.4rem',
    color: 'var(--ad-text-muted)',
    fontSize: '0.875rem',
  },

  /* ── Search card ── */
  card: {
    background: 'var(--ad-surface)',
    border: '1px solid var(--ad-border)',
    borderRadius: 'var(--ad-radius)',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  searchRow: {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.35rem',
    flex: 1,
    minWidth: 200,
  },
  label: {
    fontSize: '0.72rem',
    fontFamily: 'var(--ad-mono)',
    color: 'var(--ad-text-muted)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  input: {
    background: 'var(--ad-surface-3)',
    border: '1px solid var(--ad-border)',
    borderRadius: 'var(--ad-radius-sm)',
    color: 'var(--ad-text)',
    fontFamily: 'var(--ad-font)',
    fontSize: '0.9rem',
    padding: '0.6rem 0.85rem',
    outline: 'none',
    transition: 'border-color var(--ad-transition)',
    width: '100%',
    boxSizing: 'border-box',
  },
  select: {
    background: 'var(--ad-surface-3)',
    border: '1px solid var(--ad-border)',
    borderRadius: 'var(--ad-radius-sm)',
    color: 'var(--ad-text)',
    fontFamily: 'var(--ad-mono)',
    fontSize: '0.82rem',
    padding: '0.6rem 0.85rem',
    outline: 'none',
    cursor: 'pointer',
    width: '100%',
    boxSizing: 'border-box',
  },
  btnSearch: {
    background: 'var(--ad-amber)',
    color: '#0d1117',
    border: 'none',
    borderRadius: 'var(--ad-radius-sm)',
    fontFamily: 'var(--ad-font)',
    fontWeight: 600,
    fontSize: '0.875rem',
    padding: '0.62rem 1.4rem',
    cursor: 'pointer',
    transition: 'opacity var(--ad-transition)',
    whiteSpace: 'nowrap',
    alignSelf: 'flex-end',
  },
  btnClear: {
    background: 'transparent',
    color: 'var(--ad-text-muted)',
    border: '1px solid var(--ad-border)',
    borderRadius: 'var(--ad-radius-sm)',
    fontFamily: 'var(--ad-font)',
    fontSize: '0.875rem',
    padding: '0.62rem 1rem',
    cursor: 'pointer',
    transition: 'color var(--ad-transition)',
    alignSelf: 'flex-end',
  },

  /* ── Status / error ── */
  statusBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.75rem 1rem',
    borderRadius: 'var(--ad-radius-sm)',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
  errorBar: {
    background: 'rgba(248,113,113,0.10)',
    border: '1px solid rgba(248,113,113,0.30)',
    color: 'var(--ad-red)',
  },
  infoBar: {
    background: 'var(--ad-surface)',
    border: '1px solid var(--ad-border)',
    color: 'var(--ad-text-muted)',
  },

  /* ── Results table ── */
  tableWrapper: {
    overflowX: 'auto',
    borderRadius: 'var(--ad-radius)',
    border: '1px solid var(--ad-border)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.875rem',
  },
  th: {
    background: 'var(--ad-surface-2)',
    color: 'var(--ad-text-muted)',
    fontFamily: 'var(--ad-mono)',
    fontSize: '0.7rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    padding: '0.75rem 1rem',
    textAlign: 'left',
    borderBottom: '1px solid var(--ad-border)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '0.85rem 1rem',
    borderBottom: '1px solid var(--ad-border-soft)',
    verticalAlign: 'middle',
    color: 'var(--ad-text)',
  },
  tdMono: {
    fontFamily: 'var(--ad-mono)',
    fontSize: '0.78rem',
    color: 'var(--ad-text-muted)',
  },

  /* ── Role badge ── */
  badge: (role) => ({
    display: 'inline-block',
    padding: '0.18rem 0.6rem',
    borderRadius: 4,
    fontSize: '0.7rem',
    fontFamily: 'var(--ad-mono)',
    letterSpacing: '0.06em',
    fontWeight: 500,
    background: role === 'restaurantOwner' ? 'rgba(167,139,250,0.14)' : 'rgba(6,182,212,0.12)',
    color: role === 'restaurantOwner' ? 'var(--ad-violet)' : 'var(--ad-cyan)',
    border: `1px solid ${role === 'restaurantOwner' ? 'rgba(167,139,250,0.3)' : 'rgba(6,182,212,0.25)'}`,
  }),

  /* ── Toggle ── */
  toggleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  toggleTrack: (on) => ({
    position: 'relative',
    width: 40,
    height: 22,
    borderRadius: 11,
    background: on ? 'rgba(248,113,113,0.25)' : 'rgba(52,211,153,0.18)',
    border: `1px solid ${on ? 'rgba(248,113,113,0.5)' : 'rgba(52,211,153,0.4)'}`,
    cursor: 'pointer',
    transition: 'background var(--ad-transition), border-color var(--ad-transition)',
    flexShrink: 0,
  }),
  toggleThumb: (on) => ({
    position: 'absolute',
    top: 2,
    left: on ? 20 : 2,
    width: 16,
    height: 16,
    borderRadius: '50%',
    background: on ? 'var(--ad-red)' : 'var(--ad-emerald)',
    transition: 'left var(--ad-transition), background var(--ad-transition)',
    boxShadow: on ? '0 0 6px rgba(248,113,113,0.6)' : '0 0 6px rgba(52,211,153,0.5)',
  }),
  toggleLabel: (on) => ({
    fontSize: '0.75rem',
    fontFamily: 'var(--ad-mono)',
    color: on ? 'var(--ad-red)' : 'var(--ad-emerald)',
    whiteSpace: 'nowrap',
  }),

  /* ── Pagination ── */
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginTop: '1.25rem',
  },
  pageInfo: {
    fontSize: '0.78rem',
    fontFamily: 'var(--ad-mono)',
    color: 'var(--ad-text-muted)',
  },
  pageButtons: {
    display: 'flex',
    gap: '0.4rem',
  },
  pageBtn: (active, disabled) => ({
    background: active ? 'var(--ad-amber-dim)' : 'var(--ad-surface-2)',
    border: `1px solid ${active ? 'var(--ad-amber)' : 'var(--ad-border)'}`,
    color: active ? 'var(--ad-amber)' : disabled ? 'var(--ad-text-faint)' : 'var(--ad-text-muted)',
    borderRadius: 6,
    fontFamily: 'var(--ad-mono)',
    fontSize: '0.78rem',
    padding: '0.35rem 0.7rem',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all var(--ad-transition)',
  }),

  /* ── Skeleton ── */
  skeletonRow: {
    background: 'var(--ad-surface-2)',
    borderRadius: 4,
    animation: 'pulse 1.5s ease-in-out infinite',
  },

  /* ── Empty state ── */
  empty: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: 'var(--ad-text-faint)',
  },
  emptyIcon: {
    fontSize: '2.5rem',
    marginBottom: '0.75rem',
  },
  emptyText: {
    fontSize: '0.9rem',
    color: 'var(--ad-text-muted)',
  },
};

/* ─────────────────────────────────────────────
   Constants
───────────────────────────────────────────── */
const SEARCH_OPTIONS = [
  { value: 'email',    label: 'Email' },
  { value: 'id',       label: 'User ID' },
  { value: 'username', label: 'Username' },
  { value: 'name',     label: 'Name' },
];
const PAGE_LIMIT = 8;

/* ─────────────────────────────────────────────
   API helpers  (adjust BASE_URL as needed)
───────────────────────────────────────────── */
const BASE_URL = `${host}/admin/users`;

async function apiSearch({ q, by, page }) {
  const params = new URLSearchParams({ q, by, page, limit: PAGE_LIMIT });
  const res = await fetch(`${BASE_URL}/search?${params}`, { credentials: 'include' });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Search failed');
  return json;
}

async function apiToggleRestrict(userId, isRestricted) {
  const res = await fetch(`${BASE_URL}/${userId}/restrict`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    // userId sent in body as well so the backend can cross-verify
    body: JSON.stringify({ userId, isRestricted }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.message || 'Update failed');
  return json;
}

/* ─────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────── */
function Toggle({ userId, checked, onToggle, loading }) {
  return (
    <div style={S.toggleWrap}>
      <button
        style={S.toggleTrack(checked)}
        onClick={() => !loading && onToggle(userId, !checked)}
        aria-pressed={checked}
        aria-label={checked ? 'Unrestrict user' : 'Restrict user'}
        disabled={loading}
        title={checked ? 'Click to unrestrict' : 'Click to restrict'}
      >
        <span style={S.toggleThumb(checked)} />
      </button>
      <span style={S.toggleLabel(checked)}>
        {loading ? '…' : checked ? 'Restricted' : 'Active'}
      </span>
    </div>
  );
}

function SkeletonRows({ count = 4 }) {
  return Array.from({ length: count }).map((_, i) => (
    <tr key={i}>
      {[160, 120, 100, 80, 90].map((w, j) => (
        <td key={j} style={S.td}>
          <div style={{ ...S.skeletonRow, height: 14, width: w, maxWidth: '100%' }} />
        </td>
      ))}
    </tr>
  ));
}

/* ─────────────────────────────────────────────
   Main Component
───────────────────────────────────────────── */
const UsersList = () => {
  const [query,      setQuery]      = useState('');
  const [searchBy,   setSearchBy]   = useState('email');
  const [users,      setUsers]      = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page,       setPage]       = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [searched,   setSearched]   = useState(false);
  const [toggling,   setToggling]   = useState({}); // { [userId]: true }

  const inputRef = useRef(null);

  /* Focus input on mount */
  useEffect(() => { inputRef.current?.focus(); }, []);

  /* ── Search handler ── */
  const handleSearch = useCallback(async (overridePage = 1) => {
    if (!query.trim()) { setError('Please enter a search value.'); return; }
    setError('');
    setLoading(true);
    setSearched(true);
    try {
      const result = await apiSearch({ q: query.trim(), by: searchBy, page: overridePage });
      setUsers(result.data);
      setPagination(result.pagination);
      setPage(overridePage);
    } catch (e) {
      setError(e.message);
      setUsers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [query, searchBy]);

  /* ── Page change ── */
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > (pagination?.totalPages || 1)) return;
    handleSearch(newPage);
  };

  /* ── Restrict toggle ── */
  const handleToggle = useCallback(async (userId, newValue) => {
    setToggling(prev => ({ ...prev, [userId]: true }));
    setError('');
    try {
      const result = await apiToggleRestrict(userId, newValue);
      setUsers(prev =>
        prev.map(u => u._id === userId ? { ...u, isRestricted: result.data.isRestricted } : u)
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setToggling(prev => { const n = { ...prev }; delete n[userId]; return n; });
    }
  }, []);

  /* ── Clear ── */
  const handleClear = () => {
    setQuery('');
    setSearchBy('email');
    setUsers([]);
    setPagination(null);
    setError('');
    setSearched(false);
    setPage(1);
    inputRef.current?.focus();
  };

  /* ── Key handler ── */
  const handleKeyDown = (e) => { if (e.key === 'Enter') handleSearch(1); };

  /* ── Pagination range ── */
  const totalPages = pagination?.totalPages || 1;
  const pageNumbers = Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
    if (totalPages <= 5) return i + 1;
    if (page <= 3) return i + 1;
    if (page >= totalPages - 2) return totalPages - 4 + i;
    return page - 2 + i;
  });

  return (
    <>
      {/* Keyframe injection */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.9; }
        }
        .ad-input:focus {
          border-color: var(--ad-amber) !important;
          box-shadow: 0 0 0 3px var(--ad-amber-glow);
        }
        .ad-select:focus {
          border-color: var(--ad-amber) !important;
        }
        .ad-btn-search:hover:not(:disabled) { opacity: 0.85; }
        .ad-btn-clear:hover { color: var(--ad-text) !important; }
        .ad-tr:hover td { background: var(--ad-surface-2); }
        .ad-page-btn:hover:not(:disabled) {
          border-color: var(--ad-amber) !important;
          color: var(--ad-amber) !important;
        }
      `}</style>

      <div style={S.page}>
        <div style={S.container}>

          {/* ── Header ── */}
          {/* <div style={S.header}>
            <div style={S.headerLabel}>Admin Panel</div>
            <h1 style={S.headerTitle}>User Management</h1>
            <p style={S.headerSub}>Search and manage customers &amp; restaurant owners</p>
          </div> */}

          {/* ── Search Card ── */}
          <div style={S.card}>
            <div style={S.searchRow}>
              {/* Search-by selector */}
              <div style={{ ...S.fieldGroup, maxWidth: 160, flex: '0 0 auto' }}>
                <label style={S.label}>Search by</label>
                <select
                  className="ad-select"
                  style={S.select}
                  value={searchBy}
                  onChange={e => { setSearchBy(e.target.value); setError(''); }}
                >
                  {SEARCH_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {/* Query input */}
              <div style={S.fieldGroup}>
                <label style={S.label}>
                  {SEARCH_OPTIONS.find(o => o.value === searchBy)?.label} value
                </label>
                <input
                  ref={inputRef}
                  className="ad-input"
                  style={S.input}
                  type="text"
                  placeholder={
                    searchBy === 'email'    ? 'john@example.com' :
                    searchBy === 'id'       ? '64f3a2b1c9e...' :
                    searchBy === 'username' ? 'john_doe' :
                                             'John Doe'
                  }
                  value={query}
                  onChange={e => { setQuery(e.target.value); setError(''); }}
                  onKeyDown={handleKeyDown}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>

              {/* Buttons */}
              <button
                className="ad-btn-search"
                style={S.btnSearch}
                onClick={() => handleSearch(1)}
                disabled={loading}
              >
                {loading ? 'Searching…' : 'Search'}
              </button>

              {searched && (
                <button
                  className="ad-btn-clear"
                  style={S.btnClear}
                  onClick={handleClear}
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* ── Error / Info ── */}
          {error && (
            <div style={{ ...S.statusBar, ...S.errorBar }}>
              <span>⚠</span> {error}
            </div>
          )}

          {/* ── Results ── */}
          {(loading || users.length > 0 || (searched && !loading)) && (
            <>
              {/* Result count */}
              {!loading && searched && pagination && (
                <div style={{ ...S.statusBar, ...S.infoBar }}>
                  <span>◎</span>
                  {pagination.total === 0
                    ? 'No users found.'
                    : `${pagination.total} user${pagination.total !== 1 ? 's' : ''} found — showing page ${page} of ${pagination.totalPages}`}
                </div>
              )}

              {/* Table */}
              {(loading || users.length > 0) && (
                <div style={S.tableWrapper}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Name</th>
                        <th style={S.th}>Email</th>
                        <th style={S.th}>Username</th>
                        <th style={S.th}>Role</th>
                        <th style={S.th}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading
                        ? <SkeletonRows count={PAGE_LIMIT} />
                        : users.map(user => (
                          <tr key={user._id} className="ad-tr">
                            {/* Name */}
                            <td style={S.td}>
                              <div style={{ fontWeight: 500 }}>
                                {[user.firstName, user.lastName].filter(Boolean).join(' ') || '—'}
                              </div>
                              {user.displayName && (
                                <div style={{ fontSize: '0.75rem', color: 'var(--ad-text-muted)', marginTop: 2 }}>
                                  {user.displayName}
                                </div>
                              )}
                            </td>

                            {/* Email */}
                            <td style={{ ...S.td, ...S.tdMono }}>
                              {user.email}
                            </td>

                            {/* Username */}
                            <td style={{ ...S.td, ...S.tdMono }}>
                              {user.username || <span style={{ color: 'var(--ad-text-faint)' }}>—</span>}
                            </td>

                            {/* Role badge */}
                            <td style={S.td}>
                              <span style={S.badge(user.role)}>
                                {user.role === 'restaurantOwner' ? 'Owner' : 'Customer'}
                              </span>
                            </td>

                            {/* Restrict toggle */}
                            <td style={S.td}>
                              <Toggle
                                userId={user._id}
                                checked={user.isRestricted}
                                onToggle={handleToggle}
                                loading={!!toggling[user._id]}
                              />
                            </td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              )}

              {/* Empty state */}
              {!loading && searched && users.length === 0 && !error && (
                <div style={S.empty}>
                  <div style={S.emptyIcon}>◌</div>
                  <div style={S.emptyText}>No users match your search.</div>
                </div>
              )}

              {/* Pagination */}
              {!loading && pagination && pagination.totalPages > 1 && (
                <div style={S.pagination}>
                  <span style={S.pageInfo}>
                    {((page - 1) * PAGE_LIMIT) + 1}–{Math.min(page * PAGE_LIMIT, pagination.total)} of {pagination.total}
                  </span>
                  <div style={S.pageButtons}>
                    <button
                      className="ad-page-btn"
                      style={S.pageBtn(false, page === 1)}
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1}
                    >‹</button>

                    {pageNumbers.map(n => (
                      <button
                        key={n}
                        className="ad-page-btn"
                        style={S.pageBtn(n === page, false)}
                        onClick={() => handlePageChange(n)}
                      >{n}</button>
                    ))}

                    <button
                      className="ad-page-btn"
                      style={S.pageBtn(false, page === totalPages)}
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages}
                    >›</button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Initial idle state */}
          {!searched && !loading && (
            <div style={S.empty}>
              <div style={S.emptyIcon}>⌕</div>
              <div style={S.emptyText}>Enter a query above to search for users.</div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

export default UsersList;